// Story Library - Maps story IDs to their Twee code and metadata
export const storyLibrary = {
  'default-forest': {
    id: 'default-forest',
    title: 'Default Story (Mysterious Forest)',
    category: 'Default',
    tweeCode: `:: StoryTitle
My Interactive Story

:: Start
You wake up in a **mysterious forest**. The morning mist swirls around ancient trees, and you hear distant sounds echoing through the woods.

The trees are *ancient and gnarled*, their bark covered in strange moss.

!What do you do?

[[Explore deeper into the forest|Forest]]
[[Look for a path out|Path]]
[[Call out for help|CallOut]]

:: Forest
You venture deeper into the forest. The trees grow denser, and shafts of sunlight pierce through the canopy above. You notice ~~faint~~ strange markings on some of the tree trunks.

As you walk, you come across a small clearing with a peculiar stone altar in the center.

[[Examine the altar|Altar]]
[[Continue walking|DeepForest]]
[[Return to start|Start]]

:: Path
You search for a way out and discover a narrow dirt path leading through the undergrowth. Following it, you emerge onto a wider trail that seems to lead toward civilization.

After walking for several minutes, you see **smoke rising** in the distance.

[[Head toward the smoke|Village]]
[[Stay on the main path|MainPath]]
[[Go back|Start]]

:: CallOut
You cup your hands around your mouth and call out loudly. Your voice echoes through the trees, but there's no immediate response.

After a moment of silence, you hear rustling in the bushes nearby.

[[Investigate the sound|Investigate]]
[[Run away|Start]]
[[Call out again|CallOut2]]

:: Altar
The stone altar is covered in moss and ancient carvings. In the center, there's a small depression that might have once held something valuable.

You notice the carvings seem to tell a story of an *ancient civilization* that once lived in this forest.

[[Take a closer look at the carvings|Carvings]]
[[Leave the altar alone|Forest]]

:: DeepForest
You continue deeper into the forest. The air grows cooler and the sounds of civilization fade completely. You're truly alone now.

This might not have been the best choice.

[[Turn back|Forest]]

:: Village
Following the smoke, you discover a small village nestled in the forest. Friendly locals greet you and offer you food and directions back to the main road.

**Congratulations!** You've found your way to safety.

[[Start over|Start]]

:: MainPath
You follow the main path for what feels like hours. Eventually, you reach a highway and flag down a passing car.

You've made it out!

[[Start over|Start]]

:: Investigate
Approaching cautiously, you push aside the bushes to find a small, frightened deer. It looks at you with wide eyes before bounding away into the forest.

[[Continue exploring|Forest]]
[[Head back|Start]]

:: CallOut2
You call out again, louder this time. This time, you hear a response! A park ranger emerges from the trees, looking relieved to have found you.

The ranger guides you safely out of the forest.

[[Start over|Start]]

:: Carvings
Studying the carvings more closely, you begin to understand the story they tell. The ancient people who lived here believed the forest was *sacred* and protected by spirits.

You feel a deep connection to this place and its history.

[[Return to exploring|Forest]]
[[Head back to start|Start]]`
  },
  'educational-quiz': {
    id: 'educational-quiz',
    title: '🎓 Interactive Science Quiz ⚡ Advanced',
    category: 'Education',
    tweeCode: `:: StoryTitle
Interactive Science Quiz

:: Start
<<set $score = 0>>
⚠️ **ADVANCED EXAMPLE** - This story uses variables and scoring!

**To enable score tracking:**
1. Go to **Step 2 → Story Formatting**
2. Check **"⚡ Enable JavaScript (Variables & Logic)"**
3. Then compile and play

---

Welcome to the Solar System Quiz! 🌍

Test your knowledge about our cosmic neighborhood.

**Score: $score / 3**

Let's start with an easy one: **How many planets are in our solar system?**

[[7 planets|Wrong7]]
[[8 planets|Correct8]]
[[9 planets|Wrong9]]

:: Wrong7
Not quite! That was the old count before we discovered more about our solar system.

There are actually **8 recognized planets** (since Pluto was reclassified as a dwarf planet in 2006).

**Score: $score / 3**

[[Try the next question|Question2]]

:: Correct8
<<set $score += 1>>
✅ **Correct!** There are 8 planets in our solar system.

Since 2006, Pluto has been classified as a "dwarf planet."

**Score: $score / 3**

[[Next question →|Question2]]

:: Wrong9
That was true until 2006! Pluto was reclassified as a dwarf planet.

The correct answer is **8 planets**.

**Score: $score / 3**

[[Try the next question|Question2]]

:: Question2
Great! Next question:

**Score: $score / 3**

**Which planet is known as the Red Planet?**

[[Venus|WrongVenus]]
[[Mars|CorrectMars]]
[[Jupiter|WrongJupiter]]

:: WrongVenus
Not quite! Venus is often called Earth's "sister planet" because of its similar size.

The Red Planet is **Mars**, which gets its color from iron oxide (rust) on its surface.

**Score: $score / 3**

[[Continue learning|Question3]]

:: CorrectMars
<<set $score += 1>>
✅ **Perfect!** Mars is called the Red Planet because of the iron oxide on its surface.

You're doing great! 🎉

**Score: $score / 3**

[[Final question →|Question3]]

:: WrongJupiter
Not this time! Jupiter is known as the "Gas Giant."

**Mars** is the Red Planet due to its rusty, iron-rich soil.

**Score: $score / 3**

[[Keep going|Question3]]

:: Question3
Last question! 

**Score: $score / 3**

**Which planet has the most moons?**

[[Saturn|WrongSaturn]]
[[Jupiter|CorrectJupiter]]
[[Neptune|WrongNeptune]]

:: WrongSaturn
Close! Saturn has many moons (over 80), but not the most.

**Jupiter** holds the record with over 90 known moons!

[[See your results|Results]]

:: CorrectJupiter
<<set $score += 1>>
✅ **Excellent!** Jupiter has the most moons - over 90 discovered so far!

[[See your results|Results]]

:: WrongNeptune
Not quite! Neptune has about 14 known moons.

**Jupiter** has the most with over 90 moons!

[[See your results|Results]]

:: Results
🎓 **Quiz Complete!**

**Your Final Score: $score / 3**

<<if $score is 3>>
🌟 **Perfect Score!** You're a space expert!
<<elseif $score is 2>>
⭐ **Great Job!** You know your planets!
<<elseif $score is 1>>
✨ **Good Effort!** Keep learning about space!
<<else>>
💫 **Nice Try!** Want to learn more about our solar system?
<</if>>

[[Start over|Start]]`
  },
  'product-finder': {
    id: 'product-finder',
    title: '☕ Coffee Finder Quiz',
    category: 'E-commerce',
    tweeCode: `:: StoryTitle
Find Your Perfect Coffee

:: Start
Welcome to **Bean & Brew** Coffee Finder! ☕

Let's discover your perfect coffee match.

**How do you usually drink your coffee?**

[[Black (no milk or sugar)|Black]]
[[With milk or cream|Milk]]
[[Sweet & creamy|Sweet]]

:: Black
Great! You enjoy the pure coffee taste. ☕

**What flavor profile do you prefer?**

[[Bold & strong|BoldBlack]]
[[Smooth & balanced|SmoothBlack]]
[[Light & fruity|LightBlack]]

:: BoldBlack
**Perfect match! We recommend:**

🔥 **Dark Roast Espresso Blend**
- Intense, full-bodied flavor
- Perfect for morning wake-up
- Price: $16.99

[[Shop now|End]]
[[Try another path|Start]]

:: SmoothBlack
**Excellent choice! Try:**

✨ **House Blend Medium Roast**
- Balanced, no bitterness
- Clean finish
- Price: $14.99

[[Shop now|End]]
[[Explore more|Start]]

:: LightBlack
**You'll love:**

🌸 **Ethiopian Light Roast**
- Bright, fruity notes
- Floral aroma
- Price: $17.99

[[Shop now|End]]
[[See other options|Start]]

:: Milk
You like a creamy cup! ☕

**How much milk do you prefer?**

[[Just a splash|Splash]]
[[Half milk, half coffee (latte-style)|Latte]]

:: Splash
**Perfect! We suggest:**

🥛 **Colombian Medium Roast**
- Smooth with milk
- Nutty undertones
- Price: $15.99

[[Add to cart|End]]
[[Browse more|Start]]

:: Latte
**Great for lattes! Try:**

☁️ **Espresso Blend**
- Rich crema
- Sweet with milk
- Price: $16.99

[[Shop now|End]]
[[Keep exploring|Start]]

:: Sweet
You enjoy a treat! 🍰

**Recommended:**

🍮 **Vanilla Caramel Blend**
- Naturally sweet flavor
- Dessert-like
- Price: $18.99

[[Add to cart|End]]
[[See more options|Start]]

:: End
Thank you for using our coffee finder! 

🎁 **Use code FINDER10 for 10% off your first order**

[[Start over|Start]]`
  },
  'safety-training': {
    id: 'safety-training',
    title: '⚠️ Workplace Safety Training',
    category: 'Training',
    tweeCode: `:: StoryTitle
Factory Safety Training

:: Start
**First Day on the Factory Floor** ⚠️

You're starting your first shift at the warehouse. Your supervisor shows you to the forklift area.

"Before you operate the forklift, what should you do first?"

[[Put on my safety vest and hard hat|SafetyGear]]
[[Jump right in—I've watched YouTube tutorials|NoGear]]

:: SafetyGear
✅ **Good choice!** PPE is always the first priority.

Your supervisor nods approvingly. "Now, before starting the forklift, what's next?"

[[Inspect the forklift for damage|Inspect]]
[[Start it up and get to work|SkipInspection]]

:: NoGear
❌ Your supervisor stops you immediately.

"Stop! Personal Protective Equipment is mandatory in this area. Even minor accidents can cause serious injuries without proper gear."

**Let's try again with the right approach.**

[[Put on safety gear|SafetyGear]]

:: Inspect
✅ **Excellent!** Daily inspections prevent 90% of forklift accidents.

You check:
- ✓ Brakes working
- ✓ Horn functional
- ✓ Lights operational
- ✓ Fluid levels good

Everything looks safe!

[[Proceed with caution|Success]]

:: SkipInspection
❌ Your supervisor intervenes again.

"Hold on. OSHA requires daily inspections. Last month, someone skipped this step and discovered their brakes were faulty—mid-operation."

**Safety protocols exist for a reason.**

[[Perform the inspection|Inspect]]

:: Success
🎉 **Training Complete!**

You've successfully demonstrated:
1. ✅ Always wear PPE before entering work areas
2. ✅ Daily equipment inspections save lives
3. ✅ Never skip safety steps to save time

**You're ready for your first shift!**

[[Review training|Start]]`
  },
  'customer-journey': {
    id: 'customer-journey',
    title: '🛍️ Interactive Product Demo',
    category: 'Marketing',
    tweeCode: `:: StoryTitle
Discover SmartHome Pro

:: Start
Welcome to **SmartHome Pro** 🏠

Tired of juggling multiple apps to control your home?

*Imagine controlling everything from one simple app...*

[[Learn more|WhatIs]]
[[See pricing|Pricing]]
[[I'm not interested|NotInterested]]

:: WhatIs
**SmartHome Pro** connects all your devices:

✓ Lights 💡
✓ Thermostat 🌡️
✓ Security 🔒
✓ Entertainment 📺

**One app. Complete control.**

[[How does it work?|HowItWorks]]
[[Show me the benefits|Benefits]]

:: HowItWorks
**Setup in 3 Easy Steps:**

1️⃣ Download the app (2 minutes)
2️⃣ Connect your devices (5 minutes)
3️⃣ Create automation routines (fun!)

No technical skills needed!

[[What are the benefits?|Benefits]]
[[How much does it cost?|Pricing]]

:: Benefits
**Save Time & Money** 💰

✨ Reduce energy bills by 30%
✨ Never leave lights on again
✨ Automate your entire routine

**Real customer:** "I save $85/month on electricity!"

[[I want this!|Pricing]]
[[Tell me more|MoreInfo]]

:: MoreInfo
**Why customers love us:**

⭐⭐⭐⭐⭐ "Changed my life!"
⭐⭐⭐⭐⭐ "So easy to use"
⭐⭐⭐⭐⭐ "Saves me money"

**30-day money-back guarantee**

[[Check pricing|Pricing]]
[[Start free trial|Trial]]

:: Pricing
**SmartHome Pro Plans:**

🏠 **Basic** - $9.99/month
(Control up to 10 devices)

🏡 **Pro** - $19.99/month
(Unlimited devices + AI routines)

🏰 **Family** - $29.99/month
(Everything + Multi-home support)

[[Start free 14-day trial|Trial]]
[[I'm convinced!|Checkout]]

:: Trial
🎉 **Great choice!**

Your 14-day free trial includes:
✓ Full Pro features
✓ No credit card required
✓ Cancel anytime

[[Start my trial now|ThankYou]]

:: Checkout
🛒 **Ready to transform your home!**

Redirecting to secure checkout...

(In a real scenario, this would link to your payment page)

[[Back to start|Start]]

:: NotInterested
No problem! 

Here's a **20% discount code** for when you're ready: **SMARTHOME20**

Thanks for stopping by!

[[Actually, tell me more|WhatIs]]
[[Exit|End]]

:: ThankYou
✅ **You're all set!**

Check your email for setup instructions.

Welcome to the SmartHome family! 🎉

[[Start over|Start]]

:: End
Thanks for visiting SmartHome Pro!

[[Start over|Start]]`
  },
  'mystery-story': {
    id: 'mystery-story',
    title: '🔍 The Missing Manuscript',
    category: 'Entertainment',
    tweeCode: `:: StoryTitle
The Missing Manuscript

:: Start
**The Library That Wasn't There** 📚

You're researching ancient texts at the university library when you notice a door you've never seen before.

The brass plate reads: *"Special Collections - By Appointment Only"*

The door is slightly ajar...

[[Push the door open|OpenDoor]]
[[Ask the librarian first|AskLibrarian]]
[[Leave and come back later|Leave]]

:: OpenDoor
You push the door open. Inside, dusty shelves hold countless rare books and manuscripts.

In the center of the room, an *ancient leather-bound book* lies open on a reading stand, as if someone just left.

The page shows a hand-drawn map with strange symbols.

[[Examine the map closely|ExamineMap]]
[[Look for the librarian|LookForLibrarian]]
[[Take a photo of the map|TakePhoto]]

:: AskLibrarian
You approach the front desk. The librarian looks up, puzzled.

"Special Collections? There's no such room in this library. You must be mistaken."

But you *definitely* saw the door...

[[Insist you saw it|Insist]]
[[Go back and check|OpenDoor]]

:: ExamineMap
The map shows the university campus, but with buildings that don't exist... or do they?

One building is marked with a red X: **"The Old Observatory - 1887"**

You know the observatory was demolished in the 1960s. Or was it?

[[Search for the observatory|Observatory]]
[[Read more of the manuscript|ReadMore]]

:: LookForLibrarian
You call out, but no one answers. The room feels frozen in time.

Suddenly, you notice the dust on the shelves is *undisturbed* - except for one book that's been recently moved.

[[Pull out the moved book|FindBook]]
[[Leave the room|Leave]]

:: TakePhoto
You pull out your phone, but it's completely dead - even though it was fully charged.

*Strange...*

[[Examine the map without your phone|ExamineMap]]
[[Leave this place|Leave]]

:: Insist
The librarian frowns and checks the computer.

"That's odd... there IS a Special Collections room in the system. Room 217B. But I've worked here for 20 years and never seen it."

[[Show her the door|ShowDoor]]
[[Investigate alone|OpenDoor]]

:: Observatory
You leave the library and head to where the observatory once stood. 

To your surprise, there it is - an old stone building hidden behind overgrown ivy!

**You've discovered something impossible.**

[[Enter the observatory|ObservatoryInside]]
[[Go back to the library|OpenDoor]]

:: ReadMore
You carefully turn the pages. The manuscript describes:

*"The spaces between spaces. Places that exist in memory, waiting to be found again..."*

This is **incredible** research!

[[Take notes|TakeNotes]]
[[Continue reading|MorePages]]

:: FindBook
You pull out the book. Its title: **"Lost Places of the University"**

Inside, there's a bookmark with today's date - but the book looks centuries old.

[[Open to the bookmarked page|Bookmark]]
[[Put it back and leave|Leave]]

:: ShowDoor
You lead the librarian to the corridor. 

The door is gone. Just solid wall.

She looks at you with concern. "Perhaps you need some rest..."

**But you know what you saw.**

[[Search the library at night|NightSearch]]
[[Give up|End]]

:: ObservatoryInside
Inside, everything is perfectly preserved - as if the building exists in a time bubble.

On the desk: a letter addressed to **you**.

[[Read the letter|Letter]]

:: TakeNotes
You document everything. This could be your greatest discovery!

**Achievement Unlocked: The Researcher** 📝

[[Continue exploring|MorePages]]
[[Return tomorrow with equipment|End]]

:: MorePages
The final page contains instructions:

*"To find what's lost, one must be willing to be lost themselves."*

**Deep.**

[[Contemplate this wisdom|End]]

:: Bookmark
The bookmarked page shows a photo of Special Collections - with *you* in the picture, from today.

**How is this possible?**

[[Investigate further|Mystery]]
[[Run away screaming|End]]

:: NightSearch
You return at midnight. The door appears again, glowing faintly...

*This is just the beginning of your adventure...*

**To be continued...**

[[Start over|Start]]

:: Letter
*"Dear Seeker,*

*You found it. The places between places. Not everyone can.*

*The manuscript is yours to protect now.*

*Return it when the next seeker arrives.*

*- The Previous Guardian"*

**You are now the Guardian of Lost Places** 🔮

[[Accept the role|Guardian]]
[[This is too weird|End]]

:: Guardian
🎉 **Congratulations!**

You've become the Guardian of Special Collections.

Your adventure is just beginning...

[[Start over|Start]]

:: Mystery
The mystery deepens...

*Some questions are better left unanswered.*

[[Or are they?|NightSearch]]

:: Leave
You decide this is too strange and leave the library.

But you can't stop thinking about that room...

[[Return to investigate|Start]]

:: End
**Thanks for playing The Missing Manuscript!**

*Did you find all the secrets?*

[[Play again|Start]]`
  },
  'customer-journey-tracking': {
    id: 'customer-journey-tracking',
    title: '🛍️ Interactive Product Demo (Tracking)',
    category: 'Marketing',
    isTracking: true,
    runtimeStory: {
      id: "customer-journey-demo",
      title: "Discover SmartHome Pro",
      nodes: [
        {
          id: "start",
          title: "Welcome to SmartHome Pro 🏠",
          body: "<p>Tired of juggling multiple apps to control your home?</p><p><em>Imagine controlling everything from one simple app...</em></p>",
          choices: [
            { id: "c1", label: "Learn more", nextNodeId: "what-is" },
            { id: "c2", label: "See pricing", nextNodeId: "pricing" },
            { id: "c3", label: "I'm not interested", nextNodeId: "not-interested" }
          ]
        },
        {
          id: "what-is",
          title: "What is SmartHome Pro?",
          body: "<p><strong>SmartHome Pro</strong> connects all your devices:</p><ul><li>✓ Lights 💡</li><li>✓ Thermostat 🌡️</li><li>✓ Security 🔒</li><li>✓ Entertainment 📺</li></ul><p><strong>One app. Complete control.</strong></p>",
          choices: [
            { id: "c4", label: "How does it work?", nextNodeId: "how-it-works" },
            { id: "c5", label: "Show me the benefits", nextNodeId: "benefits" }
          ]
        },
        {
          id: "how-it-works",
          title: "Setup in 3 Easy Steps",
          body: "<p><strong>1️⃣</strong> Download the app (2 minutes)</p><p><strong>2️⃣</strong> Connect your devices (5 minutes)</p><p><strong>3️⃣</strong> Create automation routines (fun!)</p><p>No technical skills needed!</p>",
          choices: [
            { id: "c6", label: "What are the benefits?", nextNodeId: "benefits" },
            { id: "c7", label: "How much does it cost?", nextNodeId: "pricing" }
          ]
        },
        {
          id: "benefits",
          title: "Save Time & Money 💰",
          body: "<p>✨ Reduce energy bills by 30%</p><p>✨ Never leave lights on again</p><p>✨ Automate your entire routine</p><p><strong>Real customer:</strong> \"I save $85/month on electricity!\"</p>",
          choices: [
            { id: "c8", label: "I want this!", nextNodeId: "pricing" },
            { id: "c9", label: "Tell me more", nextNodeId: "more-info" }
          ]
        },
        {
          id: "more-info",
          title: "Why Customers Love Us",
          body: "<p>⭐⭐⭐⭐⭐ \"Changed my life!\"</p><p>⭐⭐⭐⭐⭐ \"So easy to use\"</p><p>⭐⭐⭐⭐⭐ \"Saves me money\"</p><p><strong>30-day money-back guarantee</strong></p>",
          choices: [
            { id: "c10", label: "Check pricing", nextNodeId: "pricing" },
            { id: "c11", label: "Start free trial", nextNodeId: "trial" }
          ]
        },
        {
          id: "pricing",
          title: "SmartHome Pro Plans",
          body: "<p>🏠 <strong>Basic</strong> - $9.99/month<br>(Control up to 10 devices)</p><p>🏡 <strong>Pro</strong> - $19.99/month<br>(Unlimited devices + AI routines)</p><p>🏰 <strong>Family</strong> - $29.99/month<br>(Everything + Multi-home support)</p>",
          choices: [
            { id: "c12", label: "Start free 14-day trial", nextNodeId: "trial" },
            { id: "c13", label: "I'm convinced!", nextNodeId: "checkout" }
          ]
        },
        {
          id: "trial",
          title: "Great Choice! 🎉",
          body: "<p>Your 14-day free trial includes:</p><ul><li>✓ Full Pro features</li><li>✓ No credit card required</li><li>✓ Cancel anytime</li></ul>",
          choices: [
            { id: "c14", label: "Start my trial now", nextNodeId: "thank-you" }
          ]
        },
        {
          id: "checkout",
          title: "Ready to Transform Your Home! 🛒",
          body: "<p>Redirecting to secure checkout...</p><p>(In a real scenario, this would link to your payment page)</p>",
          choices: []
        },
        {
          id: "not-interested",
          title: "No Problem!",
          body: "<p>Here's a <strong>20% discount code</strong> for when you're ready: <strong>SMARTHOME20</strong></p><p>Thanks for stopping by!</p>",
          choices: [
            { id: "c15", label: "Actually, tell me more", nextNodeId: "what-is" }
          ]
        },
        {
          id: "thank-you",
          title: "You're All Set! ✅",
          body: "<p>Check your email for setup instructions.</p><p>Welcome to the SmartHome family! 🎉</p>",
          choices: []
        }
      ],
      settings: {
        theme: "light",
        primaryColor: "#667eea"
      }
    }
  },
  'mystery-story-tracking': {
    id: 'mystery-story-tracking',
    title: '🔍 The Missing Manuscript (Tracking)',
    category: 'Entertainment',
    isTracking: true,
    runtimeStory: {
      id: "missing-manuscript",
      title: "The Missing Manuscript",
      nodes: [
        {
          id: "start",
          title: "The Library That Wasn't There 📚",
          body: "<p>You're researching ancient texts at the university library when you notice a door you've never seen before.</p><p>The brass plate reads: <em>\"Special Collections - By Appointment Only\"</em></p><p>The door is slightly ajar...</p>",
          choices: [
            { id: "c1", label: "Push the door open", nextNodeId: "open-door" },
            { id: "c2", label: "Ask the librarian first", nextNodeId: "ask-librarian" },
            { id: "c3", label: "Leave and come back later", nextNodeId: "leave" }
          ]
        },
        {
          id: "open-door",
          title: "A Hidden Collection",
          body: "<p>You push the door open. Inside, dusty shelves hold countless rare books and manuscripts.</p><p>In the center of the room, an <em>ancient leather-bound book</em> lies open on a reading stand, as if someone just left.</p><p>The page shows a hand-drawn map with strange symbols.</p>",
          choices: [
            { id: "c4", label: "Examine the map closely", nextNodeId: "examine-map" },
            { id: "c5", label: "Look for the librarian", nextNodeId: "look-librarian" },
            { id: "c6", label: "Take a photo of the map", nextNodeId: "take-photo" }
          ]
        },
        {
          id: "ask-librarian",
          title: "A Puzzling Response",
          body: "<p>You approach the front desk. The librarian looks up, puzzled.</p><p>\"Special Collections? There's no such room in this library. You must be mistaken.\"</p><p>But you <em>definitely</em> saw the door...</p>",
          choices: [
            { id: "c7", label: "Insist you saw it", nextNodeId: "insist" },
            { id: "c8", label: "Go back and check", nextNodeId: "open-door" }
          ]
        },
        {
          id: "examine-map",
          title: "A Mysterious Map",
          body: "<p>The map shows the university campus, but with buildings that don't exist... or do they?</p><p>One building is marked with a red X: <strong>\"The Old Observatory - 1887\"</strong></p><p>You know the observatory was demolished in the 1960s. Or was it?</p>",
          choices: [
            { id: "c9", label: "Search for the observatory", nextNodeId: "observatory" },
            { id: "c10", label: "Read more of the manuscript", nextNodeId: "read-more" }
          ]
        },
        {
          id: "look-librarian",
          title: "An Eerie Silence",
          body: "<p>You call out, but no one answers. The room feels frozen in time.</p><p>Suddenly, you notice the dust on the shelves is <em>undisturbed</em> - except for one book that's been recently moved.</p>",
          choices: [
            { id: "c11", label: "Pull out the moved book", nextNodeId: "find-book" },
            { id: "c12", label: "Leave the room", nextNodeId: "leave" }
          ]
        },
        {
          id: "take-photo",
          title: "Technology Fails",
          body: "<p>You pull out your phone, but it's completely dead - even though it was fully charged.</p><p><em>Strange...</em></p>",
          choices: [
            { id: "c13", label: "Examine the map without your phone", nextNodeId: "examine-map" },
            { id: "c14", label: "Leave this place", nextNodeId: "leave" }
          ]
        },
        {
          id: "insist",
          title: "A System Anomaly",
          body: "<p>The librarian frowns and checks the computer.</p><p>\"That's odd... there IS a Special Collections room in the system. Room 217B. But I've worked here for 20 years and never seen it.\"</p>",
          choices: [
            { id: "c15", label: "Show her the door", nextNodeId: "show-door" },
            { id: "c16", label: "Investigate alone", nextNodeId: "open-door" }
          ]
        },
        {
          id: "observatory",
          title: "The Impossible Building",
          body: "<p>You leave the library and head to where the observatory once stood.</p><p>To your surprise, there it is - an old stone building hidden behind overgrown ivy!</p><p><strong>You've discovered something impossible.</strong></p>",
          choices: [
            { id: "c17", label: "Enter the observatory", nextNodeId: "observatory-inside" },
            { id: "c18", label: "Go back to the library", nextNodeId: "open-door" }
          ]
        },
        {
          id: "read-more",
          title: "Ancient Wisdom",
          body: "<p>You carefully turn the pages. The manuscript describes:</p><p><em>\"The spaces between spaces. Places that exist in memory, waiting to be found again...\"</em></p><p>This is <strong>incredible</strong> research!</p>",
          choices: [
            { id: "c19", label: "Take notes", nextNodeId: "take-notes" },
            { id: "c20", label: "Continue reading", nextNodeId: "more-pages" }
          ]
        },
        {
          id: "find-book",
          title: "Lost Places of the University",
          body: "<p>You pull out the book. Its title: <strong>\"Lost Places of the University\"</strong></p><p>Inside, there's a bookmark with today's date - but the book looks centuries old.</p>",
          choices: [
            { id: "c21", label: "Open to the bookmarked page", nextNodeId: "bookmark" },
            { id: "c22", label: "Put it back and leave", nextNodeId: "leave" }
          ]
        },
        {
          id: "show-door",
          title: "It Vanished",
          body: "<p>You lead the librarian to the corridor.</p><p>The door is gone. Just solid wall.</p><p>She looks at you with concern. \"Perhaps you need some rest...\"</p><p><strong>But you know what you saw.</strong></p>",
          choices: [
            { id: "c23", label: "Search the library at night", nextNodeId: "night-search" },
            { id: "c24", label: "Give up", nextNodeId: "end" }
          ]
        },
        {
          id: "observatory-inside",
          title: "A Letter For You",
          body: "<p>Inside, everything is perfectly preserved - as if the building exists in a time bubble.</p><p>On the desk: a letter addressed to <strong>you</strong>.</p>",
          choices: [
            { id: "c25", label: "Read the letter", nextNodeId: "letter" }
          ]
        },
        {
          id: "take-notes",
          title: "The Researcher",
          body: "<p>You document everything. This could be your greatest discovery!</p><p><strong>Achievement Unlocked: The Researcher</strong> 📝</p>",
          choices: [
            { id: "c26", label: "Continue exploring", nextNodeId: "more-pages" }
          ]
        },
        {
          id: "more-pages",
          title: "Profound Wisdom",
          body: "<p>The final page contains instructions:</p><p><em>\"To find what's lost, one must be willing to be lost themselves.\"</em></p><p><strong>Deep.</strong></p>",
          choices: []
        },
        {
          id: "bookmark",
          title: "The Impossible Photo",
          body: "<p>The bookmarked page shows a photo of Special Collections - with <em>you</em> in the picture, from today.</p><p><strong>How is this possible?</strong></p>",
          choices: [
            { id: "c27", label: "Investigate further", nextNodeId: "mystery" }
          ]
        },
        {
          id: "night-search",
          title: "To Be Continued...",
          body: "<p>You return at midnight. The door appears again, glowing faintly...</p><p><em>This is just the beginning of your adventure...</em></p><p><strong>To be continued...</strong></p>",
          choices: []
        },
        {
          id: "letter",
          title: "The Guardian's Role",
          body: "<p><em>\"Dear Seeker,</em></p><p><em>You found it. The places between places. Not everyone can.</em></p><p><em>The manuscript is yours to protect now.</em></p><p><em>Return it when the next seeker arrives.</em></p><p><em>- The Previous Guardian\"</em></p><p><strong>You are now the Guardian of Lost Places</strong> 🔮</p>",
          choices: []
        },
        {
          id: "mystery",
          title: "Questions and Answers",
          body: "<p>The mystery deepens...</p><p><em>Some questions are better left unanswered.</em></p><p>Or are they?</p>",
          choices: []
        },
        {
          id: "leave",
          title: "An Unsettling Feeling",
          body: "<p>You decide this is too strange and leave the library.</p><p>But you can't stop thinking about that room...</p>",
          choices: [
            { id: "c28", label: "Return to investigate", nextNodeId: "start" }
          ]
        },
        {
          id: "end",
          title: "Thanks for Playing!",
          body: "<p><strong>Thanks for playing The Missing Manuscript!</strong></p><p><em>Did you find all the secrets?</em></p>",
          choices: []
        }
      ],
      settings: {
        theme: "light",
        primaryColor: "#667eea"
      }
    }
  },
  'pathnav-demo': {
    id: 'pathnav-demo',
    title: '🗺️ PathNav Demo (Pro)',
    category: 'Pro Features',
    isPathNav: true,
    pathNavConfig: {
      defaultViewMode: 'linear',
      allowViewSwitching: true,
      nodeStyle: {
        shape: 'rounded',
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        borderColor: '#6366f1',
        borderWidth: 2,
        borderStyle: 'solid',
        shadow: { enabled: true, color: 'rgba(0,0,0,0.15)', blur: 8, offsetX: 2, offsetY: 2 }
      },
      connectionStyle: {
        type: 'curved',
        color: '#9ca3af',
        width: 2,
        style: 'solid',
        arrowhead: true
      }
    },
    runtimeStory: {
      id: "pathnav-demo",
      title: "Explore PathNav Features",
      nodes: [
        {
          id: "start",
          title: "Welcome to PathNav",
          body: "<p>PathNav provides <strong>interactive mind-map navigation</strong> for your website.</p><p>Choose how you'd like to explore:</p>",
          choices: [
            { id: "c1", label: "Learn about features", nextNodeId: "features" },
            { id: "c2", label: "See it in action", nextNodeId: "demo" },
            { id: "c3", label: "Export options", nextNodeId: "export" }
          ]
        },
        {
          id: "features",
          title: "PathNav Features",
          body: "<p><strong>Dual View Modes:</strong></p><ul><li>📋 Linear navigation with breadcrumbs</li><li>🗺️ Interactive mind map view</li></ul><p><strong>Customization:</strong></p><ul><li>Custom node shapes and colors</li><li>Curved or straight connections</li><li>Expand/collapse controls</li></ul>",
          choices: [
            { id: "c4", label: "View customization options", nextNodeId: "customize" },
            { id: "c5", label: "See export options", nextNodeId: "export" }
          ]
        },
        {
          id: "customize",
          title: "Visual Customization",
          body: "<p>Customize every aspect:</p><ul><li>🎨 Node shapes: Rectangle, Rounded, Oval, Circle</li><li>🎨 Colors: Background, text, borders</li><li>✨ Shadows: Blur, offset, color</li><li>🔗 Connections: Straight, curved, orthogonal</li></ul>",
          choices: [
            { id: "c6", label: "Back to features", nextNodeId: "features" },
            { id: "c7", label: "Export options", nextNodeId: "export" }
          ]
        },
        {
          id: "demo",
          title: "Interactive Demo",
          body: "<p>You're experiencing PathNav right now!</p><p>Try switching between <strong>Linear View</strong> and <strong>Mind Map View</strong> using the toggle above.</p><p>In Mind Map mode, you can:</p><ul><li>Zoom and pan</li><li>Expand/collapse nodes</li><li>Click nodes to navigate</li></ul>",
          choices: [
            { id: "c8", label: "Learn more features", nextNodeId: "features" },
            { id: "c9", label: "Export options", nextNodeId: "export" }
          ]
        },
        {
          id: "export",
          title: "Export Levels",
          body: "<p>PathNav supports three export options:</p><p><strong>1️⃣ Enhanced HTML</strong> - Standalone file with local analytics</p><p><strong>2️⃣ WordPress Plugin</strong> - Install on any WP site</p><p><strong>3️⃣ React Package</strong> - Integrate with your React app</p>",
          choices: [
            { id: "c10", label: "HTML details", nextNodeId: "html-export" },
            { id: "c11", label: "WordPress details", nextNodeId: "wp-export" },
            { id: "c12", label: "React details", nextNodeId: "react-export" }
          ]
        },
        {
          id: "html-export",
          title: "HTML Export",
          body: "<p><strong>Perfect for:</strong> Static sites, demos, prototypes</p><p><strong>Includes:</strong></p><ul><li>Single HTML file</li><li>Local analytics dashboard</li><li>No backend required</li><li>Works offline</li></ul>",
          choices: [
            { id: "c13", label: "Back to export options", nextNodeId: "export" },
            { id: "c14", label: "Start exploring", nextNodeId: "start" }
          ]
        },
        {
          id: "wp-export",
          title: "WordPress Plugin",
          body: "<p><strong>Perfect for:</strong> WordPress sites</p><p><strong>Includes:</strong></p><ul><li>Easy installation</li><li>Admin dashboard</li><li>Database storage</li><li>Shortcode support</li></ul>",
          choices: [
            { id: "c15", label: "Back to export options", nextNodeId: "export" },
            { id: "c16", label: "Start exploring", nextNodeId: "start" }
          ]
        },
        {
          id: "react-export",
          title: "React Package",
          body: "<p><strong>Perfect for:</strong> React apps, Next.js, custom platforms</p><p><strong>Includes:</strong></p><ul><li>React component</li><li>Backend templates</li><li>Full customization</li><li>Your own hosting</li></ul>",
          choices: [
            { id: "c17", label: "Back to export options", nextNodeId: "export" },
            { id: "c18", label: "Start exploring", nextNodeId: "start" }
          ]
        }
      ],
      settings: {
        theme: "light",
        primaryColor: "#6366f1"
      }
    }
  }
};

// Get a list of all stories for the dropdown
export const getStoryOptions = () => {
  return Object.values(storyLibrary).map(story => ({
    value: story.id,
    label: story.title,
    category: story.category,
    isTracking: story.isTracking || false,
    isPathNav: story.isPathNav || false
  }));
};

// Get story by ID
export const getStoryById = (id) => {
  return storyLibrary[id] || null;
};
